/**
 * Camille & Sophie — theme-side analytics layer.
 *
 * Publishes storefront-only interaction events through `Shopify.analytics.publish()`
 * so the Custom Pixel (see `analytics/custom-pixel.js`) can translate them into GA4
 * events from a single place.
 *
 * Ecommerce events (page_view, view_item_list, select_item, view_item, add_to_cart,
 * remove_from_cart, view_cart, begin_checkout, purchase) are NOT published here —
 * they come from Shopify's native Standard Events, which the pixel subscribes to.
 */
(function () {
  'use strict';

  const BRAND = 'Camille & Sophie';
  const LIST_KEY = 'cs:last_item_list';

  const EVENTS = {
    productContext: 'cs_product_context',
    selectItem: 'cs_select_item',
    clickWhatsapp: 'cs_click_whatsapp',
    viewSizeGuide: 'cs_view_size_guide',
    selectSize: 'cs_select_size',
    search: 'cs_search',
    filterApply: 'cs_filter_apply',
    clickBanner: 'cs_click_campaign_banner',
    generateLead: 'cs_generate_lead',
  };

  function publish(name, payload) {
    try {
      window.Shopify?.analytics?.publish?.(name, Object.assign(pageContext(), payload || {}));
    } catch (error) {
      console.debug('[analytics] publish failed', name, error);
    }
  }

  function pageContext() {
    return {
      page_location: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      page_template: document.querySelector('main[data-template]')?.dataset.template || '',
    };
  }

  function parseJsonScript(selector) {
    const node = document.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.debug('[analytics] could not parse', selector, error);
      return null;
    }
  }

  /* ------------------------------------------------------------------ *
   * Item list memory — the native product_added_to_cart payload doesn't
   * carry the originating collection, so remember the last list a product
   * was opened from and hand it to the pixel with the product context.
   * ------------------------------------------------------------------ */

  function rememberList(listName, listId) {
    if (!listName) return;
    try {
      sessionStorage.setItem(LIST_KEY, JSON.stringify({ item_list_name: listName, item_list_id: listId || '' }));
    } catch (error) {
      // sessionStorage can throw in private mode — the list name is optional context.
    }
  }

  function recallList() {
    try {
      return JSON.parse(sessionStorage.getItem(LIST_KEY)) || {};
    } catch (error) {
      return {};
    }
  }

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="/products/"]');
    if (!link) return;

    const list = link.closest('[data-analytics-list-name]');
    if (list) rememberList(list.dataset.analyticsListName, list.dataset.analyticsListId);

    // select_item — published from the card rather than read from a native event,
    // so it carries the category taxonomy metafields the native payloads lack.
    const card = link.closest('[data-analytics-item-id]');
    if (!card) return;

    publish(EVENTS.selectItem, {
      item_id: card.dataset.analyticsItemId,
      item_name: card.dataset.analyticsItemName,
      item_brand: BRAND,
      item_category: card.dataset.analyticsItemCategory || '',
      item_category2: card.dataset.analyticsItemCategory2 || '',
      item_category3: card.dataset.analyticsItemCategory3 || '',
      price: parseFloat(card.dataset.analyticsPrice) || 0,
      product_id: card.dataset.analyticsProductId,
      ...recallList(),
    });
  });

  /* ------------------------------------------------------------------ *
   * Product context — pushes the taxonomy metafields and the variant map
   * of the current product page so the pixel can enrich the native
   * ecommerce events (which carry no metafields) and, later, checkout.
   * ------------------------------------------------------------------ */

  let productContext = null;

  function getProductContext() {
    if (productContext === null) productContext = parseJsonScript('#AnalyticsProductContext') || false;
    return productContext || null;
  }

  function productBaseItem() {
    const context = getProductContext();
    if (!context) return {};
    return {
      item_id: context.sku || String(context.variant_id || ''),
      item_name: context.item_name || '',
      item_brand: BRAND,
      item_category: context.item_category || '',
      item_category2: context.item_category2 || '',
      item_category3: context.item_category3 || '',
      product_id: context.product_id,
    };
  }

  function publishProductContext() {
    const context = getProductContext();
    if (!context) return;

    publish(EVENTS.productContext, {
      product_id: context.product_id,
      item_name: context.item_name,
      item_brand: BRAND,
      item_category: context.item_category || '',
      item_category2: context.item_category2 || '',
      item_category3: context.item_category3 || '',
      currency: context.currency,
      variants: (context.variants || []).map((variant) => ({
        variant_id: variant.id,
        item_id: variant.sku || String(variant.id),
        item_variant: variant.title,
        price: (variant.price || 0) / 100,
        available: variant.available,
      })),
      ...recallList(),
    });
  }

  /* ------------------------------------------------------------------ *
   * click_whatsapp
   * ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-analytics-whatsapp]');
    if (!trigger) return;

    publish(EVENTS.clickWhatsapp, {
      button_location: trigger.dataset.analyticsWhatsapp || 'floating',
      link_url: trigger.getAttribute('href') || '',
    });
  });

  /* ------------------------------------------------------------------ *
   * view_size_guide
   * ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-analytics-size-guide]');
    if (!trigger) return;

    publish(EVENTS.viewSizeGuide, {
      source: trigger.dataset.analyticsSizeGuide || 'unknown',
      ...productBaseItem(),
    });
  });

  /* ------------------------------------------------------------------ *
   * select_size — fires on every variant (talla) change on a product page.
   * ------------------------------------------------------------------ */

  function sizeOptionValue(variant) {
    const context = getProductContext();
    const names = context?.option_names || [];
    const index = names.findIndex((name) => /talla|size/i.test(name));
    if (index === -1 || !Array.isArray(variant.options)) return variant.title || '';
    return variant.options[index] || variant.title || '';
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant) return;

      publish(EVENTS.selectSize, {
        ...productBaseItem(),
        item_id: variant.sku || String(variant.id),
        item_variant: variant.title || '',
        size: sizeOptionValue(variant),
        availability: variant.available ? 'in_stock' : 'out_of_stock',
        price: (variant.price || 0) / 100,
        currency: getProductContext()?.currency || '',
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * search — one event per real search: the results page render, or a
   * predictive-search suggestion click (which never reaches that page).
   * Keystrokes in the typeahead deliberately do not publish.
   * ------------------------------------------------------------------ */

  const publishedSearchTerms = new Set();

  function publishSearch(term, resultsCount, source) {
    const searchTerm = (term || '').trim();
    if (!searchTerm) return;

    const key = `${source}:${searchTerm.toLowerCase()}`;
    if (publishedSearchTerms.has(key)) return;
    publishedSearchTerms.add(key);

    publish(EVENTS.search, {
      search_term: searchTerm,
      results_count: Number.isFinite(resultsCount) ? resultsCount : null,
      search_source: source,
    });
  }

  function publishSearchResultsPage() {
    const results = document.querySelector('[data-analytics-search-term]');
    if (!results) return;
    publishSearch(
      results.dataset.analyticsSearchTerm,
      parseInt(results.dataset.analyticsSearchResults, 10),
      'results_page'
    );
  }

  document.addEventListener('click', (event) => {
    const predictive = event.target.closest('predictive-search');
    if (!predictive || !event.target.closest('[data-predictive-search] a')) return;

    const total = predictive.querySelector('[data-total-results]')?.dataset.totalResults;
    publishSearch(predictive.querySelector('input[type="search"]')?.value, parseInt(total, 10), 'predictive');
  });

  /* ------------------------------------------------------------------ *
   * filter_apply — `facets:updated` is dispatched by assets/facets.js once
   * the filtered grid (and its product count) has been rendered.
   * ------------------------------------------------------------------ */

  let pendingFilter = null;

  function filterTypeFromName(name) {
    if (!name) return '';
    if (name === 'sort_by') return 'sort_by';
    // filter.v.option.talla -> talla, filter.v.price.gte -> price
    const parts = name.replace(/^filter\.[a-z]\./, '').split('.');
    return parts[0] || name;
  }

  document.addEventListener('change', (event) => {
    const input = event.target.closest('facet-filters-form input, facet-filters-form select');
    if (!input || !input.name) return;

    pendingFilter = {
      filter_type: filterTypeFromName(input.name),
      filter_name: input.name,
      filter_value: input.type === 'checkbox' ? input.value : input.value || '',
      filter_action: input.type === 'checkbox' && !input.checked ? 'remove' : 'apply',
    };
  });

  document.addEventListener('facets:updated', (event) => {
    if (!pendingFilter) return;
    const filter = pendingFilter;
    pendingFilter = null;

    publish(EVENTS.filterApply, {
      ...filter,
      results_count: event.detail?.count ?? null,
      list_name: document.querySelector('[data-analytics-list-name]')?.dataset.analyticsListName || '',
    });
  });

  /* ------------------------------------------------------------------ *
   * click_campaign_banner
   * ------------------------------------------------------------------ */

  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-analytics-banner]');
    if (!link) return;

    publish(EVENTS.clickBanner, {
      banner_type: link.dataset.analyticsBanner,
      creative_name: link.dataset.analyticsBannerLabel || link.getAttribute('href') || '',
      creative_slot: link.dataset.analyticsBannerSlot || '',
      promotion_id: link.dataset.analyticsBannerId || '',
      link_url: link.getAttribute('href') || '',
    });
  });

  /* ------------------------------------------------------------------ *
   * generate_lead — published from the success state Shopify renders after
   * the form POST, so the event survives the page reload the form triggers.
   * ------------------------------------------------------------------ */

  function publishLeads() {
    document.querySelectorAll('[data-analytics-lead-success]').forEach((marker) => {
      const form = marker.closest('[data-analytics-lead]');
      if (!form) return;

      publish(EVENTS.generateLead, {
        form_type: form.dataset.analyticsLead,
        form_location: form.dataset.formLocation || '',
      });
    });
  }

  /* ------------------------------------------------------------------ */

  function init() {
    publishProductContext();
    publishSearchResultsPage();
    publishLeads();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
